import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { BarChart3, Edit } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsManagement = () => {
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    totalSales: '', netProfit: '', activeAds: '',
    inTransit: '', delivered: '', returned: '',
    deliveredSales: '', productCost: '', deliveryCharges: '', grossMargin: '',
    adsSpend: '', rtoCharges: ''
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = async (user) => {
    setSelectedUser(user);
    try {
      const response = await axios.get(`${API}/analytics/${user.principalId}`);
      if (response.data) {
        setFormData({
          totalSales: response.data.totalSales || '',
          netProfit: response.data.netProfit || '',
          activeAds: response.data.activeAds || '',
          inTransit: response.data.inTransit || '',
          delivered: response.data.delivered || '',
          returned: response.data.returned || '',
          deliveredSales: response.data.deliveredSales || '',
          productCost: response.data.productCost || '',
          deliveryCharges: response.data.deliveryCharges || '',
          grossMargin: response.data.grossMargin || '',
          adsSpend: response.data.adsSpend || '',
          rtoCharges: response.data.rtoCharges || ''
        });
      }
    } catch (error) {
      // Use empty form
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        userId: selectedUser.principalId,
        totalSales: parseFloat(formData.totalSales) || 0,
        netProfit: parseFloat(formData.netProfit) || 0,
        activeAds: parseInt(formData.activeAds) || 0,
        inTransit: parseInt(formData.inTransit) || 0,
        delivered: parseInt(formData.delivered) || 0,
        returned: parseInt(formData.returned) || 0,
        deliveredSales: parseFloat(formData.deliveredSales) || 0,
        productCost: parseFloat(formData.productCost) || 0,
        deliveryCharges: parseFloat(formData.deliveryCharges) || 0,
        grossMargin: parseFloat(formData.grossMargin) || 0,
        adsSpend: parseFloat(formData.adsSpend) || 0,
        rtoCharges: parseFloat(formData.rtoCharges) || 0
      };
      await axios.put(`${API}/admin/analytics/${selectedUser.principalId}`, data);
      toast({ title: 'Success', description: 'Analytics data updated successfully' });
      setDialogOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Update failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-blue-600">Analytics Management</h2>

      <div className="grid gap-4">
        {users.map(user => (
          <Card key={user.principalId} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">#{user.clientId} - {user.name}</p>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
                <Button size="sm" onClick={() => handleEdit(user)} className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Analytics
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-green-600">Edit Analytics Data</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-blue-400">Main Metrics</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600">Total Sales (₹)</Label>
                  <Input type="number" value={formData.totalSales} onChange={(e) => setFormData({...formData, totalSales: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">Net Profit (₹)</Label>
                  <Input type="number" value={formData.netProfit} onChange={(e) => setFormData({...formData, netProfit: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">Active Ads</Label>
                  <Input type="number" value={formData.activeAds} onChange={(e) => setFormData({...formData, activeAds: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
              </div>

              <h3 className="text-sm font-semibold text-blue-600 mt-4">Order Analytics</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600">In Transit</Label>
                  <Input type="number" value={formData.inTransit} onChange={(e) => setFormData({...formData, inTransit: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">Delivered</Label>
                  <Input type="number" value={formData.delivered} onChange={(e) => setFormData({...formData, delivered: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">Returned</Label>
                  <Input type="number" value={formData.returned} onChange={(e) => setFormData({...formData, returned: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
              </div>

              <h3 className="text-sm font-semibold text-orange-400 mt-4">Gross Margin</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Delivered Sales (₹)</Label>
                  <Input type="number" value={formData.deliveredSales} onChange={(e) => setFormData({...formData, deliveredSales: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">Product Cost (₹)</Label>
                  <Input type="number" value={formData.productCost} onChange={(e) => setFormData({...formData, productCost: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">Delivery Charges (₹)</Label>
                  <Input type="number" value={formData.deliveryCharges} onChange={(e) => setFormData({...formData, deliveryCharges: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">Gross Margin (₹)</Label>
                  <Input type="number" value={formData.grossMargin} onChange={(e) => setFormData({...formData, grossMargin: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
              </div>

              <h3 className="text-sm font-semibold text-blue-500 mt-4">Net Margin</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Ads Spend (₹)</Label>
                  <Input type="number" value={formData.adsSpend} onChange={(e) => setFormData({...formData, adsSpend: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
                <div>
                  <Label className="text-slate-600">RTO Charges (₹)</Label>
                  <Input type="number" value={formData.rtoCharges} onChange={(e) => setFormData({...formData, rtoCharges: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-800" />
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600">
              Update Analytics
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalyticsManagement;